// File: nodes/UpdateChannelConsentNode.jsx
import React from 'react';
import { Handle, Position } from 'reactflow';
import {
  Box,
  Flex,
  Text,
  IconButton,
  HStack,
} from '@chakra-ui/react';
import { FiCopy, FiEdit, FiTrash2, FiToggleLeft, FiSlash } from 'react-icons/fi';
import { FaCheckCircle } from 'react-icons/fa';
import { getConsentChannel } from '../utils/consentChannels';
import { getChannelLabel } from '../../../utils/channels';

const UpdateChannelConsentNode = ({ data }) => {
  // Channels come from /channel/list now, so the label saved on the node is the
  // source of truth; the static map is only a fallback for nodes saved before that.
  const channelMeta = getConsentChannel(data.channel);
  const channelName = getChannelLabel(data.channelLabel || channelMeta?.label || '');
  const channel = channelName ? { ...(channelMeta || {}), label: channelName } : null;
  const ChannelIcon = channel?.icon;
  const isEnable = data.consentAction === 'enable';
  const isDisable = data.consentAction === 'disable';

  return (
    <Box
      bg="white"
      border="2px solid"
      borderColor="#C026D3"
      borderRadius="xl"
      boxShadow={data.selected ? 'xl' : 'lg'}
      minW="250px"
      position="relative"
    >
      <Handle
        type="target"
        position={Position.Left}
        style={{ width: 12, height: 12, background: '#C026D3' }}
      />

      {/* Header */}
      <Flex
        bg="#C026D3"
        color="white"
        px={4}
        py={2.5}
        borderTopRadius="xl"
        align="center"
        gap={2}
      >
        <FiToggleLeft size={16} />
        <Text noOfLines={1} flex={1} fontSize="sm" fontWeight="medium">
          Update Channel Consent
        </Text>
        <HStack spacing={1}>
          <IconButton
            icon={<FiEdit size={14} />}
            size="xs"
            variant="ghost"
            color="white"
            _hover={{ bg: 'whiteAlpha.300' }}
            onClick={(e) => {
              e.stopPropagation();
              data.onEdit?.(data.id);
            }}
            aria-label="Edit"
          />
          <IconButton
            icon={<FiCopy size={14} />}
            size="xs"
            variant="ghost"
            color="white"
            _hover={{ bg: 'whiteAlpha.300' }}
            onClick={(e) => {
              e.stopPropagation();
              data.onDuplicate?.(data.id);
            }}
            aria-label="Duplicate"
          />
          <IconButton
            icon={<FiTrash2 size={14} />}
            size="xs"
            variant="ghost"
            color="white"
            _hover={{ bg: 'whiteAlpha.300' }}
            onClick={(e) => {
              e.stopPropagation();
              data.onDelete?.(data.id);
            }}
            aria-label="Delete"
          />
        </HStack>
      </Flex>

      {/* Body */}
      <Box p={3}>
        <Text fontSize="sm" fontWeight="medium" mb={3}>
          {data.label || 'Update Channel Consent'}
        </Text>

        {/* Channel */}
        <Flex align="center" gap={2} mb={2}>
          {channel ? (
            <Flex
              flex={1}
              align="center"
              gap={1.5}
              bg="pink.50"
              border="1px solid"
              borderColor="pink.200"
              borderRadius="lg"
              px={2.5}
              py={1.5}
            >
              {/* dynamic channels have no static icon — render the name alone */}
              {ChannelIcon && (
                <Box color="pink.600" flexShrink={0}>
                  <ChannelIcon size={13} />
                </Box>
              )}
              <Text fontSize="xs" fontWeight="semibold" color="pink.900">
                {channel.label}
              </Text>
            </Flex>
          ) : (
            <Flex
              flex={1}
              justify="center"
              bg="gray.50"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="lg"
              px={2.5}
              py={1.5}
            >
              <Text fontSize="xs" color="gray.500">
                No channel
              </Text>
            </Flex>
          )}
        </Flex>

        {/* Consent action */}
        {(isEnable || isDisable) && (
          <Flex
            align="center"
            gap={2}
            px={3}
            py={2}
            borderRadius="lg"
            border="1px solid"
            borderColor={isEnable ? 'green.200' : 'red.200'}
            bg={isEnable ? 'green.50' : 'red.50'}
          >
            <Box color={isEnable ? 'green.600' : 'red.500'} flexShrink={0}>
              {isEnable ? <FaCheckCircle size={13} /> : <FiSlash size={13} />}
            </Box>
            <Text
              fontSize="xs"
              fontWeight="semibold"
              color={isEnable ? 'green.700' : 'red.600'}
            >
              {isEnable ? 'Enable Opt-In' : 'Disable Opt-In'}
            </Text>
          </Flex>
        )}

        {!channel && !isEnable && !isDisable && (
          <Text fontSize="xs" color="gray.500" textAlign="center" py={1}>
            Not configured
          </Text>
        )}
      </Box>

      <Handle
        type="source"
        position={Position.Right}
        style={{ width: 12, height: 12, background: '#C026D3' }}
      />
    </Box>
  );
};

export default UpdateChannelConsentNode;
